import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import moment from 'moment';
import { useState } from 'react';
import { RiArrowLeftLine } from '@remixicon/react';
import { Badge, Button } from '@tremor/react';
import { Vacancy } from '../../../../network/ats';
import { DeleteDialog } from '../Dialogs/DeleteDialog';
import EditVacancy from './EditVacancy';

const salaryText = (vacancy: Vacancy) => {
  const range = [vacancy.minimumSalary, vacancy.maximumSalary]
    .filter(Boolean)
    .join(' – ');
  if (!range) return null;
  return [range, vacancy.currency, vacancy.salaryTime?.toLowerCase()]
    .filter(Boolean)
    .join(' ');
};

export const VacancyItem = ({ vacancy }: { vacancy: Vacancy }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  if (isEditing) {
    return (
      <div>
        <Button
          icon={RiArrowLeftLine}
          onClick={() => setIsEditing(false)}
          className="mb-4 border-gray-300 bg-white text-gray-900"
          iconPosition="left"
          variant="secondary"
        >
          Back to vacancies
        </Button>
        <EditVacancy vacancy={vacancy} onDone={() => setIsEditing(false)} />
      </div>
    );
  }

  const salary = salaryText(vacancy);

  return (
    <div>
      <DeleteDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        vacancy={vacancy}
      />
      <div className="flex flex-col justify-between gap-4 rounded-lg bg-white p-4 shadow ring-1 ring-black/5 sm:flex-row sm:p-6">
        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex flex-wrap gap-2">
            <Badge color={vacancy.status === 'closed' ? 'gray' : 'green'}>
              {vacancy.status === 'closed' ? 'Closed' : 'Open'}
            </Badge>
            <Badge color={vacancy.visibility === 'public' ? 'blue' : 'gray'}>
              {vacancy.visibility === 'public' ? 'Public' : 'Private'}
            </Badge>
            {vacancy.dateCreated && (
              <Badge color="yellow">
                Posted {moment(vacancy.dateCreated).format('D MMM YYYY')}
              </Badge>
            )}
          </div>
          <div className="text-xl font-semibold text-gray-900">
            {vacancy.jobTitle}
          </div>
          {vacancy.description && (
            <p className="line-clamp-3 whitespace-pre-line text-base text-gray-500">
              {vacancy.description}
            </p>
          )}
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-700">
            {vacancy.jobLocation && (
              <span>
                Location: <span className="font-medium">{vacancy.jobLocation}</span>
              </span>
            )}
            {vacancy.jobType && (
              <span>
                Type: <span className="font-medium">{vacancy.jobType}</span>
              </span>
            )}
            {salary && (
              <span>
                Salary: <span className="font-medium">{salary}</span>
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-row gap-3 self-start">
          <button
            className="flex h-fit flex-nowrap rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700"
            onClick={() => setIsEditing(true)}
          >
            <PencilSquareIcon className="my-auto mr-1 h-4" />
            Edit
          </button>
          <button
            className="flex h-fit flex-nowrap rounded-lg border border-red-500 bg-white px-4 py-2.5 text-sm font-semibold text-red-500"
            onClick={() => setShowDeleteDialog(true)}
          >
            <TrashIcon className="my-auto mr-1 h-4 text-red-500" />
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};
